import { css, keyframes } from '@emotion/react';
import { theme } from '../../../design-system/theme/theme';

/** 차트 외곽: 막대 위 숫자 라벨이 들릴 상단 여백 확보. */
export const chartStyle = css`
  position: relative;
  height: 240px;
  padding-top: 16px;
`;

/** 실제 그리기 영역 — grid 선과 막대가 이 안에서 바닥 정렬로 겹쳐진다. */
export const plotStyle = css`
  position: relative;
  height: 100%;
`;

/** y축 grid 한 줄. bottom(값 비율)은 인라인. 좌측 라벨 폭(28px)만큼 띄워 그린다. */
export const gridLineStyle = css`
  position: absolute;
  left: 0px;
  right: 0;
  border-top: 1px dashed ${theme.color.border};
  pointer-events: none;
`;

/** grid 값 라벨 — 좌측 축. 선 위에 살짝 걸치게. */
export const gridLabelStyle = css`
  position: absolute;
  left: 0;
  width: 24px;
  top: -0.6em;
  text-align: right;
  font-size: 10px;
  line-height: 1;
  color: ${theme.color.textMuted};
  font-variant-numeric: tabular-nums;
  pointer-events: none;
`;

/** 막대들이 놓이는 행 — y축 라벨 폭만큼 들여쓰고, 많으면 가로 스크롤. */
export const barsRowStyle = css`
  position: absolute;
  inset: 0 0 0 28px;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  overflow-x: auto;
  /* 가로만 스크롤. overflow-x:auto가 y축을 auto로 강제하는 걸 막아 세로 스크롤바 방지. */
  overflow-y: hidden;
`;

/** 막대 한 칼럼 — 더 촘촘히(최소폭 축소). 바닥 정렬. */
export const colStyle = css`
  flex: 1 0 6px;
  min-width: 4px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  border-radius: ${theme.radius.sm}px ${theme.radius.sm}px 0 0;
  transition: background 0.12s ease;
  /* 가리키는 x축 컬럼에 세로 음영(hover band) — 회색. */
  &:hover {
    background: rgba(139, 147, 161, 0.15);
  }
`;

/** 막대 자라나는 애니메이션 — 바닥(0)에서 실제 높이까지. */
const growUp = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

/** 막대 본체 래퍼 — height(전체 비율)는 인라인. 숫자 라벨의 기준(relative). */
export const barOuterStyle = css`
  position: relative;
  width: 100%;
  max-width: 22px;
  min-height: 2px;
  /* 값 라벨이 짧은 막대 밖(흰 배경)으로 넘쳐 흐릿하게 남는 걸 방지. */
  overflow: hidden;
  border-radius: ${theme.radius.sm}px ${theme.radius.sm}px 0 0;
  /* 데이터 갱신 시 높이 변화는 부드럽게 트윈. */
  transition: height 0.5s cubic-bezier(0.22, 1, 0.36, 1);
`;

/** 막대 스택을 바닥 기준으로 자라나게(초기 마운트 시). */
export const barGrowStyle = css`
  transform-origin: bottom;
  animation: ${growUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

/** 스택 막대 — 세그먼트를 아래→위로 쌓음. */
export const barStackStyle = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  border-radius: ${theme.radius.sm}px ${theme.radius.sm}px 0 0;
  overflow: hidden;
`;

/** 스택 세그먼트 한 조각. height(막대 내 비율)·background는 인라인. */
export const segStyle = css`
  width: 100%;
`;

/** 툴팁 등장 — 아래에서 살짝 떠오르며 페이드 인. */
const tipIn = keyframes`
  from { opacity: 0; transform: translate(-50%, calc(-100% - 4px)); }
  to { opacity: 1; transform: translate(-50%, calc(-100% - 10px)); }
`;

/** 커스텀 hover 툴팁 — 기본 title(지연·스타일 불가) 대체. 커서 위에 즉시 표시. */
export const tooltipStyle = css`
  position: absolute;
  z-index: 20;
  transform: translate(-50%, calc(-100% - 10px));
  padding: 7px 11px;
  background: ${theme.color.text};
  color: ${theme.color.onPrimary};
  border-radius: ${theme.radius.sm}px;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  pointer-events: none;
  animation: ${tipIn} 0.12s ease-out;

  /* 아래쪽 꼬리(삼각형). */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: ${theme.color.text};
  }
`;

/** 툴팁 제목 = x축 값(날짜/그룹). 구분선으로 값과 분리. */
export const tooltipTitleStyle = css`
  display: block;
  font-weight: 700;
  font-size: 13px;
  padding-bottom: 5px;
  margin-bottom: 5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
`;

/** 값 행 = 캡션(무엇) + 값(y). 좌우 벌려 정렬. */
export const tooltipRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

/** 값 행 좌측 캡션(예: '건수', 세그먼트명). 살짝 흐리게. */
export const tooltipCaptionStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.75;
`;

/** 세그먼트 색 점 — 범례와 연결. */
export const tooltipDotStyle = (color: string) => css`
  width: 8px;
  height: 8px;
  border-radius: ${theme.radius.pill}px;
  background: ${color};
  flex-shrink: 0;
`;

/** 툴팁 안에서 값(y·건수)을 강조하는 부분. */
export const tooltipValueStyle = css`
  font-weight: 700;
  font-size: 14px;
`;

/** 데이터 없음 안내. */
export const emptyStyle = css`
  padding: ${theme.space.lg}px;
  text-align: center;
  color: ${theme.color.textMuted};
  font-size: ${theme.fontSize.sm}px;
`;
